import { API_BASE_URL } from '../config/api'

// Token storage keys
const ACCESS_TOKEN_KEY = 'realitni_access_token'
const REFRESH_TOKEN_KEY = 'realitni_refresh_token'
const USER_KEY = 'realitni_user'
export const AUTH_SESSION_EXPIRED_EVENT = 'realitni-auth-session-expired'

const parseAuthResponse = async (response) => {
  let body = {}
  try {
    body = await response.json()
  } catch {
    // Keep a consistent error when the API did not return JSON.
  }
  if (!response.ok) {
    const error = new Error(body.message || `HTTP error! status: ${response.status}`)
    error.status = response.status
    error.code = body.code
    error.details = body
    throw error
  }
  return body
}

export const authService = {
  // Store tokens in localStorage
  setTokens(accessToken, refreshToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },

  setAccessToken(accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  },

  // Get access token
  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  // Get refresh token
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  // Store user info
  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  },

  // Get user info
  getUser() {
    const userStr = localStorage.getItem(USER_KEY)
    return userStr ? JSON.parse(userStr) : null
  },

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },

  notifySessionExpired() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT))
  },

  // Check authentication status at startup
  async checkAuthStatus() {
    const token = this.getAccessToken()
    if (!token) {
      return false
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const userData = await response.json()
        this.setUser(userData)
        return true
      } else if (response.status === 401) {
        // Token is invalid, try to refresh
        try {
          await this.refreshToken()
          // Try again with new token
          const newToken = this.getAccessToken()
          const retryResponse = await fetch(`${API_BASE_URL}/api/users/me`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${newToken}`,
            },
          })

          if (retryResponse.ok) {
            const userData = await retryResponse.json()
            this.setUser(userData)
            return true
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          this.clearAuth()
          return false
        }
      }

      return false
    } catch (error) {
      console.error('Auth status check failed:', error)
      this.clearAuth()
      return false
    }
  },

  // Login with API
  async login(identifier, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: identifier, password }),
      })
      const data = await parseAuthResponse(response)

      // Store tokens and user info
      this.setTokens(data.token, data.refreshToken)
      this.setUser(data.user)

      return data
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  },

  async verifyEmail(token) {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    const data = await parseAuthResponse(response)
    this.setTokens(data.token, data.refreshToken)
    this.setUser(data.user)
    return data
  },

  async resendVerification(email) {
    const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    return parseAuthResponse(response)
  },

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      const data = await parseAuthResponse(response)

      // Refresh currently rotates only the access token; preserve the stored refresh token.
      this.setAccessToken(data.token)

      return data
    } catch (error) {
      console.error('Token refresh error:', error)
      this.clearAuth()
      throw error
    }
  },

  // Logout
  async logout() {
    try {
      const refreshToken = this.getRefreshToken()
      if (refreshToken) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearAuth()
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAccessToken()
    return !!token
  },

  // Get current user
  getCurrentUser() {
    return this.getUser()
  },
}
